import { Eye, Lock, ShieldAlert, ShieldCheck, Wrench } from 'lucide-react';
import type { ReactNode } from 'react';
import { TIER_DESCRIPTION } from '@orgus/protocol';
import type { CapabilityTier } from '@orgus/protocol';
import { Badge, Panel, TONE_CLASS, Tooltip, cn } from '@orgus/ui';
import { TIER_TONE, capabilitiesByTier, escalationsInto, tierLabel } from './model.ts';
import { NARROW_QUERY, useMediaQuery } from '../../lib/useMediaQuery.ts';

const TIER_ICON: Record<CapabilityTier, ReactNode> = {
  read: <Eye />,
  operate: <Wrench />,
  elevated: <ShieldAlert />,
  critical: <Lock />,
};

const WHO: Record<CapabilityTier, string> = {
  read: 'Every agent token',
  operate: 'Agent tokens by default',
  elevated: 'Only when a scope names it',
  critical: 'People only',
};

/** What an agent may do: the catalogue by tier, with who reaches each tier. */
export function TiersPanel() {
  // A phone has no room beside the title for the sub-line: it would be cut to "Scopes nar...".
  const narrow = useMediaQuery(NARROW_QUERY);
  return (
    <Panel title="What an agent may do" icon={<ShieldCheck />} sub={narrow ? undefined : "Scopes narrow a token's role, never widen it"}>
      <div className="grid gap-3 md:grid-cols-2">
        {capabilitiesByTier().map(({ tier, caps }) => {
          const tone = TIER_TONE[tier];
          const raised = escalationsInto(tier);
          return (
            <section key={tier} aria-labelledby={`tier-${tier}`} className="flex flex-col gap-2 rounded-md border border-border-subtle bg-raised p-3">
              <header className="flex items-center gap-2">
                <span className={cn('[&_svg]:size-4', TONE_CLASS[tone].fg)} aria-hidden>
                  {TIER_ICON[tier]}
                </span>
                <h3 id={`tier-${tier}`} className="font-medium text-fg">
                  {tierLabel(tier)}
                </h3>
                <Badge tone={tone} variant="outline" className="ml-auto">
                  {WHO[tier]}
                </Badge>
              </header>
              <p className="text-sm text-fg-muted">{TIER_DESCRIPTION[tier]}</p>
              <ul className="flex flex-wrap gap-1.5" aria-label={`${tierLabel(tier)} capabilities`}>
                {caps.map((c) => (
                  <li key={c.name}>
                    <Tooltip content={c.description}>
                      <span tabIndex={0} className="inline-flex rounded-sm border border-border px-1.5 py-0.5 font-mono text-xs text-fg-muted">
                        {c.name}
                      </span>
                    </Tooltip>
                  </li>
                ))}
                {raised.map(({ cap, reason }) => (
                  <li key={`${cap.name}-raised`}>
                    <Tooltip content={reason}>
                      <span tabIndex={0} className="inline-flex rounded-sm border border-dashed border-border px-1.5 py-0.5 font-mono text-xs text-fg-subtle">
                        {cap.name}, raised
                      </span>
                    </Tooltip>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
      <ul className="mt-4 flex flex-col gap-1 text-sm text-fg-muted">
        <li>Every change an agent makes is in the audit log as agent:&lt;name&gt;, with the same checks as a person's.</li>
        <li>Agents change things only through the AI API or POST /api/actions; other change routes refuse agent tokens.</li>
      </ul>
    </Panel>
  );
}
